/**
 * AdminLayout - iOS 26 Liquid Glass Navigation
 * Paliacate OS
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import axios from 'axios';
import { 
  Home, TrendingUp, Users, DollarSign, Settings, Search,
  Bell, Menu, LogOut, BookOpen, Activity, Briefcase, Clock, FileText, MessageSquare,
  CheckSquare, Star, LayoutGrid, Sun, Inbox, MoreHorizontal, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { GlobalSearch } from '../components/admin/GlobalSearch';
import UnifiedNotificationInbox from '../components/UnifiedNotificationInbox';
import { clearSession } from '../services/sessionService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CEO_NAV = [
  { id: 'hoy', label: 'Hoy', icon: Sun, path: '/admin' },
  { id: 'bandeja', label: 'Bandeja', icon: Inbox, path: '/admin/bandeja' },
  { id: 'chat', label: 'Chat', icon: MessageSquare, path: '/admin/chat/manage' },
  { id: 'clientes', label: 'Clientes', icon: Users, path: '/admin/control-operativo' },
  { id: 'tareas', label: 'Tareas', icon: CheckSquare, path: '/admin/tareas' },
  { id: 'equipo', label: 'Equipo', icon: Briefcase, path: '/admin/team' },
  { id: 'finanzas', label: 'Finanzas', icon: DollarSign, path: '/admin/finanzas' },
  { id: 'mas', label: 'Más', icon: MoreHorizontal, path: '/admin/mas' },
];

const SUPERVISOR_NAV = [
  { id: 'radar', label: 'Radar', icon: Activity, path: '/admin/supervisor' },
  { id: 'ciclos', label: 'Ciclos del Mes', icon: Clock, path: '/admin/ciclos' },
  { id: 'equipo', label: 'Equipo', icon: Users, path: '/admin/equipo' },
  { id: 'control', label: 'Control Operativo', icon: LayoutGrid, path: '/admin/control-operativo' },
  { id: 'tareas', label: 'Tareas', icon: CheckSquare, path: '/admin/tareas' },
  { id: 'clientes', label: 'Clientes', icon: Briefcase, path: '/admin/crm' },
  { id: 'encuestas', label: 'Encuestas', icon: Star, path: '/admin/encuestas' },
];

const VENTAS_NAV = [
  { id: 'mi-dia', label: 'Mi Dia', icon: Home, path: '/admin/sales-dashboard' },
  { id: 'pipeline', label: 'Pipeline', icon: TrendingUp, path: '/admin/leads' },
  { id: 'clientes', label: 'Clientes', icon: Users, path: '/admin/crm' },
  { id: 'propuestas', label: 'Propuestas', icon: FileText, path: '/admin/propuestas' },
  { id: 'cobranza', label: 'Cobranza', icon: DollarSign, path: '/admin/billing' },
];

// ============================================================
// SIDEBAR
// ============================================================

const Sidebar = ({ isOpen, onClose, currentPath, systemRole, userName, userEmail, badges }) => {
  const navigate = useNavigate();
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  
  const getNavItems = () => {
    switch (systemRole) {
      case 'supervisor': return SUPERVISOR_NAV;
      case 'ventas': return VENTAS_NAV;
      default: return CEO_NAV;
    }
  };

  const navItems = getNavItems();
  const showConfig = systemRole === 'admin';

  const handleLogout = async () => {
    await clearSession();
    toast.success('Sesion cerrada correctamente');
    navigate('/login');
  };

  const isActivePath = (itemPath) => {
    if (itemPath === '/admin' && currentPath === '/admin') return true;
    if (itemPath !== '/admin' && currentPath.startsWith(itemPath)) return true;
    return false;
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 lg:hidden" onClick={onClose} />
      )}
      
      <aside 
        className={`ios-sidebar transform transition-transform duration-300 lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="admin-sidebar"
        role="navigation"
        aria-label="Navegacion principal"
      >
        <div className="flex flex-col h-full">
          <div className="ios-sidebar-logo">
            <div className="ios-sidebar-logo-icon">P</div>
            <div className="lg:block md:hidden">
              <div className="ios-sidebar-logo-name">Paliacate</div>
              <div className="ios-sidebar-logo-sub">{systemRole || 'Admin'}</div>
            </div>
          </div>

          <nav className="ios-nav-items" role="menubar" aria-label="Menu principal">
            {navItems.map((item) => {
              const isActive = isActivePath(item.path);
              return (
                <button
                  key={item.id}
                  onClick={() => { navigate(item.path); onClose(); }}
                  className={`ios-nav-item ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${item.id}`}
                  role="menuitem"
                  aria-current={isActive ? 'page' : undefined}
                >
                  <item.icon className="ios-nav-icon w-5 h-5" />
                  <span className="lg:block md:hidden">{item.label}</span>
                  {item.id === 'tareas' && badges?.overdue > 0 && (
                    <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center" data-testid="sidebar-badge-tareas">
                      {badges.overdue}
                    </span>
                  )}
                  {item.id === 'bandeja' && badges?.unread > 0 && (
                    <span className="ml-auto w-2.5 h-2.5 rounded-full bg-orange-500" data-testid="sidebar-badge-bandeja" />
                  )}
                </button>
              );
            })}
          </nav>

          <div className="ios-nav-footer relative">
            {avatarMenuOpen && (
              <div className="absolute bottom-full left-2 right-2 mb-2 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50" data-testid="avatar-menu">
                <button
                  onClick={() => { navigate('/admin/config'); setAvatarMenuOpen(false); onClose(); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                  data-testid="avatar-menu-config"
                >
                  <Settings className="w-4 h-4" /> Config
                </button>
                <a href={`${API_URL}/api/reports/pdf/manual`} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-testid="avatar-menu-manual">
                  <BookOpen className="w-4 h-4" /> Manual
                </a>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4" /> Cerrar sesión
                </button>
              </div>
            )}
            <button
              onClick={() => setAvatarMenuOpen(!avatarMenuOpen)}
              className="ios-user-block w-full text-left hover:bg-white/40 rounded-xl transition-colors flex items-center gap-2"
              data-testid="avatar-menu-trigger"
            >
              <div className="ios-avatar-person ios-avatar-sm">{(userName || 'U').charAt(0)}</div>
              <div className="lg:block md:hidden min-w-0 flex-1">
                <div className="ios-user-name truncate">{userName || 'Usuario'}</div>
                <div className="ios-user-role truncate">{userEmail}</div>
              </div>
              <ChevronUp className={`w-4 h-4 text-gray-400 transition-transform ${avatarMenuOpen ? '' : 'rotate-180'}`} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// ============================================================
// BOTTOM NAVIGATION (Mobile)
// ============================================================

const BottomNav = ({ currentPath, systemRole }) => {
  const navigate = useNavigate();
  
  const getNavItems = () => {
    switch (systemRole) {
      case 'supervisor': return SUPERVISOR_NAV;
      case 'ventas': return VENTAS_NAV;
      default: return CEO_NAV;
    }
  };

  const navItems = getNavItems();

  const isActivePath = (itemPath) => {
    if (itemPath === '/admin' && currentPath === '/admin') return true;
    if (itemPath !== '/admin' && currentPath.startsWith(itemPath)) return true;
    return false;
  };

  return (
    <nav className="ios-bottom-nav" data-testid="bottom-nav" role="navigation" aria-label="Navegacion movil">
      {navItems.slice(0, 5).map((item) => {
        const isActive = isActivePath(item.path);
        return (
          <button
            key={item.id}
            onClick={() => navigate(item.path)}
            className={`ios-bottom-nav-item ${isActive ? 'active' : ''}`}
            data-testid={`bottom-nav-${item.id}`}
            aria-current={isActive ? 'page' : undefined}
            aria-label={item.label}
          >
            <item.icon className="w-6 h-6" />
            <span>{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
};

// ============================================================
// HEADER
// ============================================================

const Header = ({ onMenuClick, title, subtitle, badges }) => {
  const navigate = useNavigate();
  return (
    <header className="ios-header" data-testid="admin-header" role="banner">
      <div className="flex items-center gap-3 flex-1">
        <button
          onClick={onMenuClick}
          className="ios-notif-btn md:hidden"
          data-testid="menu-toggle"
          aria-label="Abrir menu"
          aria-expanded={false}
        >
          <Menu className="w-5 h-5" />
        </button>
        <div>
          <h1 className="ios-header-title">{title}</h1>
          {subtitle && <p className="ios-caption">{subtitle}</p>}
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <div className="hidden sm:block">
          <GlobalSearch />
        </div>
        <button
          onClick={() => navigate('/admin/bandeja')}
          className="ios-notif-btn relative"
          data-testid="header-bandeja-btn"
          aria-label="Bandeja"
        >
          <Bell className="w-5 h-5" />
          {badges?.unread > 0 && (
            <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-orange-500" />
          )}
        </button>
      </div>
    </header>
  );
};

// ============================================================
// MAIN LAYOUT
// ============================================================

export default function AdminLayout({ children, title, subtitle }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [badges, setBadges] = useState({ overdue: 0, unread: 0 });
  const location = useLocation();

  useEffect(() => {
    const loadBadges = async () => {
      try {
        const [stats, count] = await Promise.all([
          axios.get(`${API_URL}/api/production-tasks/stats/dashboard`, { withCredentials: true }),
          axios.get(`${API_URL}/api/unified-notifications/count`, { withCredentials: true }),
        ]);
        setBadges({ overdue: stats.data.total_overdue || 0, unread: count.data.unread || 0 });
      } catch (e) {}
    };
    loadBadges();
    const t = setInterval(loadBadges, 60000);
    return () => clearInterval(t);
  }, [location.pathname]);
  
  let systemRole = 'admin';
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  let userName = userInfo.name;
  let userEmail = userInfo.email;
  try {
    const user = JSON.parse(localStorage.getItem('portal_user') || '{}');
    systemRole = user.system_role || 'admin';
    userName = userName || user.name || '';
    userEmail = userEmail || user.email || '';
  } catch (e) {}

  useEffect(() => {
    axios.get(`${API_URL}/api/auth/me`, { withCredentials: true })
      .then(r => setUserInfo({ name: r.data?.name || '', email: r.data?.email || '' }))
      .catch(() => {});
  }, []);

  // Dynamic page title based on route
  const PAGE_TITLES = {
    '/admin': { title: 'Hoy', subtitle: '' },
    '/admin/dashboard': { title: 'Métricas', subtitle: 'Vista anterior de inicio' },
    '/admin/mi-dia': { title: 'Hoy', subtitle: '' },
    '/admin/bandeja': { title: 'Bandeja', subtitle: 'Asignaciones, menciones y avisos' },
    '/admin/mas': { title: 'Más', subtitle: 'Rutas fuera del menú principal' },
    '/admin/control-operativo': { title: 'Clientes', subtitle: 'Control operativo de campañas' },
    '/admin/team': { title: 'Equipo', subtitle: 'Colaboradores y asistencia' },
    '/admin/tareas': { title: 'Tareas', subtitle: '' },
    '/admin/ventas': { title: 'Ventas', subtitle: 'Pipeline, actividad y seguimiento comercial' },
    '/admin/crm': { title: 'CRM clásico', subtitle: 'CRM, actividad y renovaciones' },
    '/admin/operaciones': { title: 'Operaciones', subtitle: 'Produccion, entregables y equipo' },
    '/admin/finanzas': { title: 'Finanzas', subtitle: 'Cobranza y facturacion' },
    '/admin/config': { title: 'Configuracion', subtitle: 'Usuarios, permisos y sistema' },
    '/admin/leads': { title: 'Pipeline', subtitle: 'Leads y oportunidades' },
    '/admin/billing': { title: 'Cobranza', subtitle: '' },
    '/admin/team/management': { title: 'Equipo', subtitle: 'Gestion de colaboradores' },
    '/admin/analytics': { title: 'Analytics', subtitle: '' },
    '/admin/reports': { title: 'Reportes', subtitle: '' },
    '/admin/production': { title: 'Produccion', subtitle: '' },
    '/admin/chat': { title: 'Chat', subtitle: '' },
    '/admin/supervisor': { title: 'Supervisor', subtitle: '' },
  };

  const pathBase = location.pathname.replace(/\/+$/, '');
  const pageInfo = PAGE_TITLES[pathBase]
    || Object.entries(PAGE_TITLES).find(([k]) => k !== '/admin' && pathBase.startsWith(k))?.[1]
    || { title: title || 'Dashboard', subtitle: subtitle || '' };
  const pageTitle = title || pageInfo.title;
  const pageSubtitle = subtitle || pageInfo.subtitle;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        document.querySelector('[data-testid="global-search-trigger"]')?.click();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="ios-page">
      <a href="#main-content" className="ios-skip-link">Saltar al contenido</a>
      <Sidebar 
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        currentPath={location.pathname}
        systemRole={systemRole}
        userName={userName}
        userEmail={userEmail}
        badges={badges}
      />
      
      <div className="ios-main">
        <Header 
          onMenuClick={() => setSidebarOpen(true)}
          title={pageTitle}
          subtitle={pageSubtitle}
          badges={badges}
        />
        <main className="ios-content" id="main-content" role="main">
          {children || <Outlet />}
        </main>
      </div>
      
      <BottomNav currentPath={location.pathname} systemRole={systemRole} />
    </div>
  );
}

export { AdminLayout, Sidebar, BottomNav, Header };
